import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
// FR-012 lazy load (DECIDED 2026-09-04, e2e finding F1): the /pure entrypoint
// has NO import-time side effect — the default "@stripe/stripe-js" entry
// injects the Stripe.js script the moment the module is imported, which
// fetched Stripe.js on every route (admin CSP blocks it). With /pure the
// script loads only when loadStripe() runs, and loadStripe runs only on
// payment-step mount (getStripeLoader below).
import { loadStripe } from "@stripe/stripe-js/pure";
import type { ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui";

/**
 * App-styled payment-step modal (CLMS-117 T033). Renders the Stripe PAYMENT
 * ELEMENT (provider-tokenized entry; the server's named payment-method
 * configuration decides the offered methods — card only today, the SPA never
 * lists or filters methods itself) inside the shared Radix dialog (focus
 * trap, labelled title/description). No hosted redirect checkout is used
 * (FR-012): confirmation runs in-page with `redirect: "if_required"` and no
 * return/provider URL is ever constructed. The client secret arrives via
 * props, is handed only to Stripe.js in memory, and is never logged,
 * persisted, or placed in a URL. Failure messages are user-safe categories
 * only (FR-022) — provider error detail never renders.
 */

/** One Stripe.js load per publishable key; never re-created per render. */
const stripeLoaderCache = new Map<string, Promise<Stripe | null>>();

function getStripeLoader(publishableKey: string): Promise<Stripe | null> {
  let loader = stripeLoaderCache.get(publishableKey);
  if (!loader) {
    const created = loadStripe(publishableKey);
    loader = created;
    stripeLoaderCache.set(publishableKey, created);
    // Only a usable Stripe instance stays cached: a rejected or resolved-null
    // load is evicted so a later modal open retries instead of replaying the
    // failure for the rest of the session. Guarded so a retry that already
    // replaced the entry is never evicted by the stale loader settling.
    const evict = (): void => {
      if (stripeLoaderCache.get(publishableKey) === created) {
        stripeLoaderCache.delete(publishableKey);
      }
    };
    created.then((stripe) => {
      if (stripe === null) {
        evict();
      }
    }, evict);
  }
  return loader;
}

export type PaymentFailureCategory = "payment_details" | "payment_unavailable";

export const PAYMENT_FAILURE_COPY: Record<PaymentFailureCategory, string> = {
  payment_details: "Your payment could not be completed. Check your payment details and try again.",
  payment_unavailable: "We couldn't process your payment right now. Try again in a moment.",
};

export interface RegistrationPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Server-resolved display amount from the status API, shown verbatim. */
  amountDue: string;
  /** Secret of the server-created PaymentIntent (memory-only pass-through). */
  clientSecret: string;
  publishableKey: string;
  /** Provider confirmation accepted — parent shows verification-pending and resumes polling. */
  onConfirmed: () => void;
  /**
   * Mirrors the in-flight confirmPayment state to the parent so a
   * parent-initiated teardown (e.g. an authority-lost revert from a
   * background 401) can be deferred until the confirmation settles —
   * unmounting the Elements iframe mid-3DS would abort the confirm.
   */
  onConfirmingChange?: (confirming: boolean) => void;
}

export function RegistrationPaymentModal({
  open,
  onOpenChange,
  amountDue,
  clientSecret,
  publishableKey,
  onConfirmed,
  onConfirmingChange,
}: RegistrationPaymentModalProps): ReactElement {
  // Lifted so dismissal can be blocked while confirmPayment is in flight:
  // closing then would unmount the Elements iframe mid-confirmation (e.g.
  // a 3DS challenge) and onConfirmed could fire after a "cancelled" close.
  const [confirming, setConfirming] = useState(false);
  const handleConfirmingChange = (next: boolean): void => {
    setConfirming(next);
    onConfirmingChange?.(next);
  };
  const [stripeLoader, setStripeLoader] = useState(() => getStripeLoader(publishableKey));
  // Re-acquire on every open: a no-op while the cached load is healthy
  // (same promise, no re-render), a fresh Stripe.js attempt after a failed
  // load was evicted from the cache.
  useEffect(() => {
    if (open) {
      setStripeLoader(getStripeLoader(publishableKey));
    }
  }, [open, publishableKey]);
  // Stripe.js failing to load (script blocked, network) or resolving null
  // would otherwise leave the Pay button disabled forever with no feedback;
  // detect it and surface the payment_unavailable copy instead.
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);
  useEffect(() => {
    let disposed = false;
    setStripeLoadFailed(false);
    stripeLoader.then(
      (stripe) => {
        if (!disposed && stripe === null) {
          setStripeLoadFailed(true);
        }
      },
      () => {
        if (!disposed) {
          setStripeLoadFailed(true);
        }
      },
    );
    return () => {
      disposed = true;
    };
  }, [stripeLoader]);
  const options = useMemo(() => {
    // Stripe's appearance API needs a literal value: resolve the shared
    // --clm-primary-blue token at mount, falling back to its literal only
    // where it isn't defined (e.g. jsdom).
    const colorPrimary =
      getComputedStyle(document.documentElement).getPropertyValue("--clm-primary-blue").trim() ||
      "#16456b";
    return {
      clientSecret,
      appearance: {
        variables: { colorPrimary, fontSizeBase: "14px" },
      },
    };
  }, [clientSecret]);

  const handleOpenChange = (nextOpen: boolean): void => {
    // Grant-form pattern: ignore Escape/overlay/X closes during confirm.
    if (confirming) {
      return;
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby="registration-payment-description"
        onEscapeKeyDown={(event) => {
          if (confirming) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (confirming) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Pay your membership dues</DialogTitle>
          <DialogDescription id="registration-payment-description" className="text-gray-600">
            Amount due: <span className="font-medium text-gray-900">{amountDue}</span>. Payment is
            processed securely by our payment provider; card details never reach CLM.
          </DialogDescription>
        </DialogHeader>
        {stripeLoadFailed ? (
          // Initialization failed: the Payment Element can never mount, so
          // the form would be a permanently-disabled dead end. Same user-safe
          // alert as a confirm-time provider failure, with an explicit exit;
          // close/reopen retries the (evicted) Stripe.js load.
          <>
            <Alert variant="destructive" className="mt-4">
              <AlertTitle>Payment not completed</AlertTitle>
              <AlertDescription>{PAYMENT_FAILURE_COPY.payment_unavailable}</AlertDescription>
            </Alert>
            <DialogFooter className="mt-4">
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <Elements stripe={stripeLoader} options={options}>
            <PaymentModalForm
              amountDue={amountDue}
              submitting={confirming}
              onSubmittingChange={handleConfirmingChange}
              onConfirmed={onConfirmed}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaymentModalForm({
  amountDue,
  submitting,
  onSubmittingChange,
  onConfirmed,
}: {
  amountDue: string;
  submitting: boolean;
  onSubmittingChange: (submitting: boolean) => void;
  onConfirmed: () => void;
}): ReactElement {
  const stripe = useStripe();
  const elements = useElements();
  const [failureCategory, setFailureCategory] = useState<PaymentFailureCategory | null>(null);

  const handleConfirm = async (): Promise<void> => {
    if (!stripe || !elements || submitting) {
      return;
    }
    onSubmittingChange(true);
    setFailureCategory(null);
    try {
      // In-page confirmation only; card payments complete without redirect.
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: "if_required",
      });
      if (error) {
        // Closed user-safe category mapping — provider messages never render.
        setFailureCategory(
          error.type === "card_error" || error.type === "validation_error"
            ? "payment_details"
            : "payment_unavailable",
        );
        return;
      }
      onConfirmed();
    } catch {
      setFailureCategory("payment_unavailable");
    } finally {
      onSubmittingChange(false);
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void handleConfirm();
      }}
    >
      <PaymentElement />
      {failureCategory && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Payment not completed</AlertTitle>
          <AlertDescription>{PAYMENT_FAILURE_COPY[failureCategory]}</AlertDescription>
        </Alert>
      )}
      {/* SR-visible confirmation progress (established audit pattern). */}
      <p role="status" className="mt-2 text-sm text-gray-600">
        {submitting ? "Processing your payment\u2026" : null}
      </p>
      <DialogFooter className="mt-4">
        {/* Disabled until Stripe AND Elements are ready: an enabled click
            before elements resolves would silently no-op in handleConfirm. */}
        <Button
          type="submit"
          className="w-full sm:w-auto"
          disabled={!stripe || !elements || submitting}
        >
          Pay {amountDue}
        </Button>
      </DialogFooter>
    </form>
  );
}
